import { useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { MEASUREMENT_TYPES, derivedId, measurementType } from '@gk/contracts'
import { useClub, useEngineState } from '../../club/ClubProvider.tsx'
import { buildImportRows, chunkRows, suggestImportMapping, type ImportMapping, type ImportProblem } from '../../data/measureUi.ts'
import { readTableFile, sha256Hex, type ParsedTable } from '../../import/readTable.ts'
import { fmtNum } from '../../lib/format.ts'
import { formatLocal } from '../../lib/time.ts'
import { BackLink, Banner, Field, Section, errorText } from '../../ui/components.tsx'
import { lang2, typeLabel, unitSymbol, unitsFor, useAreaOptions } from './shared.tsx'

const textHash = async (s: string): Promise<string> => sha256Hex(new TextEncoder().encode(s).buffer as ArrayBuffer)

/** A file from an instrument (GK-039): read here, on the device; every row is checked exactly as a typed reading is, and the file is sent only when it has no problem. The same file twice adds nothing (the file's hash
 *  is its identity), and neither does a re-saved copy (a row already in the notebook is skipped). Weather and soil sensors export wide files: each value column becomes readings of one type and unit. */
export function MeasureImportPage() {
  const { t, i18n } = useTranslation()
  const { org, site, can, sendAndWait } = useClub()
  const online = useEngineState().online !== false
  const areas = useAreaOptions()
  const [file, setFile] = useState<{ name: string; table: ParsedTable; sha256: string } | null>(null)
  const [map, setMap] = useState<ImportMapping | null>(null)
  const [readError, setReadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ sent: number; inserted: number; duplicates: number; failed: string | null } | null>(null)
  const input = useRef<HTMLInputElement>(null)

  async function choose(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]; if (!f) return
    setReadError(null); setResult(null)
    try {
      const { table, sha256 } = await readTableFile(f)
      if (table.rows.length === 0) { setReadError(t('measures.import.empty')); setFile(null); setMap(null); return }
      setFile({ name: f.name, table, sha256 }); setMap(suggestImportMapping(table.headers))
    } catch { setReadError(t('measures.import.unreadable')); setFile(null); setMap(null) }
  }
  const checked = useMemo(() => (file && map && areas ? buildImportRows(file.table, map, areas.map((a) => ({ id: a.id, code: a.code, names: [a.name] })), site.timezone, new Date()) : null), [file, map, areas, site.timezone])
  if (!can('measurement.manage')) return <section className="flex flex-col gap-4"><h1 className="text-2xl font-bold">{t('measures.import.title')}</h1><Banner tone="warn">{t('measures.denied')}</Banner></section>
  if (!areas) return <p>{t('common.loading')}</p>

  const headers = file?.table.headers ?? []
  const colSelect = (label: string, value: string | null, onChange: (v: string | null) => void, testId: string, optional = true) => (
    <Field label={label} htmlFor={`im-${testId}`}>
      <select id={`im-${testId}`} className="input" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} data-testid={`import-${testId}`}>
        <option value="">{optional ? t('measures.import.none') : t('mounts.choose')}</option>
        {headers.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
    </Field>
  )
  const problemText = (p: ImportProblem) => {
    const where = p.row === 0 ? '' : t('measures.import.where', { row: p.row, column: p.column ?? '' })
    const what = p.code.startsWith('IMPORT_') ? t(`measures.import.problem.${p.code}`) : errorText(t, p.code)
    return `${where}${where ? ' · ' : ''}${what}${p.detail && p.row !== 0 ? ` (${p.detail})` : ''}`
  }

  async function send() {
    if (!file || !checked || checked.problems.length || !checked.rows.length) return
    setBusy(true); setResult({ sent: 0, inserted: 0, duplicates: 0, failed: null })
    const chunks = chunkRows(checked.rows)
    let inserted = 0, duplicates = 0, sent = 0
    try {
      for (let i = 0; i < chunks.length; i++) {
        const sha = chunks.length === 1 ? file.sha256 : await textHash(`${file.sha256}:${i + 1}`)
        const v = await sendAndWait('measurement.import@1', {
          batch_id: derivedId(org.org_id, sha), file_sha256: sha, file_name: chunks.length === 1 ? file.name : `${file.name} (${i + 1}/${chunks.length})`,
          rows: chunks[i]!.map((r) => ({ measurement_type: r.measurement_type, ...(r.area_id ? { area_id: r.area_id } : {}), value: r.value, unit: r.unit, observed_at: r.observed_at, ...(r.depth_mm !== null ? { depth_mm: r.depth_mm } : {}), ...(r.method ? { method: r.method } : {}), ...(r.instrument ? { instrument: r.instrument } : {}) })),
        })
        if (v.status !== 'applied' && v.status !== 'applied_with_exception') { setResult({ sent, inserted, duplicates, failed: v.status === 'queued' ? t('errors.network') : errorText(t, v.code) }); setBusy(false); return }
        const s = (v.result?.summary ?? {}) as { inserted?: number; duplicates?: number }
        inserted += s.inserted ?? 0; duplicates += s.duplicates ?? 0; sent += chunks[i]!.length
        setResult({ sent, inserted, duplicates, failed: null })
      }
    } catch (e) { setResult({ sent, inserted, duplicates, failed: e instanceof Error ? e.message : t('errors.unknown') }) }
    setBusy(false)
  }

  return (
    <section className="flex flex-col gap-5" data-testid="measure-import-page">
      <BackLink to="/measures">← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('measures.import.title')}</h1>
      <p className="meta">{t('measures.import.help')}</p>
      {!online && <Banner tone="warn">{t('common.onlineOnly')}</Banner>}
      <div className="card flex flex-col gap-3">
        <input ref={input} type="file" accept=".csv,.txt,.xlsx,text/csv" className="input" onChange={(e) => void choose(e)} data-testid="import-file" aria-label={t('measures.import.choose')} />
        {readError && <Banner tone="error"><span data-testid="import-read-error">{readError}</span></Banner>}
        {file && <p className="meta" data-testid="import-file-info">{t('measures.import.fileInfo', { name: file.name, rows: file.table.rows.length, columns: file.table.headers.length })}</p>}
      </div>

      {file && map && (
        <>
          <Section title={t('measures.import.columns')} testId="import-mapping">
            <div className="card flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                {colSelect(t('measures.import.date'), map.date, (v) => setMap({ ...map, date: v }), 'date', false)}
                {colSelect(t('measures.import.time'), map.time, (v) => setMap({ ...map, time: v }), 'time')}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {colSelect(t('measures.import.areaColumn'), map.areaColumn, (v) => setMap({ ...map, areaColumn: v }), 'area-column')}
                <Field label={t('measures.import.fixedArea')} htmlFor="im-fixed-area">
                  <select id="im-fixed-area" className="input" value={map.fixedAreaId ?? ''} onChange={(e) => setMap({ ...map, fixedAreaId: e.target.value || null })} data-testid="import-fixed-area">
                    <option value="">{t('measures.import.none')}</option>
                    {areas.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {colSelect(t('measures.import.depthColumn'), map.depthColumn, (v) => setMap({ ...map, depthColumn: v }), 'depth')}
                {colSelect(t('measures.import.instrumentColumn'), map.instrumentColumn, (v) => setMap({ ...map, instrumentColumn: v }), 'instrument-column')}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('measures.import.fixedInstrument')} htmlFor="im-inst"><input id="im-inst" className="input" maxLength={80} value={map.fixedInstrument} onChange={(e) => setMap({ ...map, fixedInstrument: e.target.value })} data-testid="import-fixed-instrument" /></Field>
                <Field label={t('measures.method')} htmlFor="im-method"><input id="im-method" className="input" maxLength={80} value={map.method} onChange={(e) => setMap({ ...map, method: e.target.value })} data-testid="import-method" /></Field>
              </div>
            </div>
          </Section>
          <Section title={t('measures.import.values')} help={t('measures.import.valuesHelp')} testId="import-values">
            <ul className="flex flex-col gap-2">
              {headers.filter((h) => ![map.date, map.time, map.areaColumn, map.depthColumn, map.instrumentColumn].includes(h)).map((h) => {
                const cur = map.values.find((v) => v.column === h)
                return (
                  <li key={h} className="card flex flex-col gap-2" data-testid="import-value-column" data-column={h}>
                    <label className="flex min-h-11 items-center gap-2 font-semibold">
                      <input type="checkbox" checked={!!cur} onChange={(e) => setMap({ ...map, values: e.target.checked ? [...map.values, { column: h, type: 'soil_moisture', unit: 'pct' }] : map.values.filter((v) => v.column !== h) })} data-testid="import-value-check" /> {h}
                    </label>
                    {cur && (
                      <div className="grid grid-cols-[1fr_auto] gap-3">
                        <select className="input" aria-label={t('measures.type')} value={cur.type} onChange={(e) => setMap({ ...map, values: map.values.map((v) => (v.column === h ? { ...v, type: e.target.value, unit: measurementType(e.target.value)!.unit } : v)) })} data-testid="import-value-type">
                          {MEASUREMENT_TYPES.map((d) => <option key={d.code} value={d.code}>{typeLabel(d, i18n.language)}</option>)}
                        </select>
                        <select className="input" aria-label={t('measures.unit')} value={cur.unit} onChange={(e) => setMap({ ...map, values: map.values.map((v) => (v.column === h ? { ...v, unit: e.target.value } : v)) })} data-testid="import-value-unit">
                          {unitsFor(cur.type).map((u) => <option key={u.code} value={u.code}>{u.symbol[lang2(i18n.language)]}</option>)}
                        </select>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </Section>

          {checked && (
            <Section title={t('measures.import.check')} testId="import-check">
              <div className="card flex flex-col gap-2">
                <p data-testid="import-count">{t('measures.import.readings', { count: checked.rows.length })}</p>
                {checked.problems.length > 0 && (
                  <>
                    <Banner tone="error"><span data-testid="import-problems-count">{t('measures.import.problems', { count: checked.problems.length })}</span></Banner>
                    <ul className="list-disc pl-5 text-sm" data-testid="import-problems">
                      {checked.problems.slice(0, 15).map((p, i) => <li key={i} data-code={p.code}>{problemText(p)}</li>)}
                    </ul>
                  </>
                )}
                {checked.problems.length === 0 && checked.rows.length > 0 && (
                  <table className="w-full text-sm" data-testid="import-sample">
                    <thead><tr className="text-left opacity-70"><th>{t('measures.type')}</th><th>{t('measures.area')}</th><th>{t('measures.value')}</th><th>{t('measures.when')}</th></tr></thead>
                    <tbody>{checked.rows.slice(0, 5).map((r, i) => (
                      <tr key={i}><td>{typeLabel(measurementType(r.measurement_type), i18n.language)}</td><td>{areas.find((a) => a.id === r.area_id)?.label ?? t('measures.site')}</td><td>{fmtNum(r.value, i18n.language, 4)} {unitSymbol(r.unit, i18n.language)}</td><td>{formatLocal(r.observed_at, site.timezone)}</td></tr>
                    ))}</tbody>
                  </table>
                )}
                {checked.rows.length > 1000 && <Banner tone="info">{t('measures.import.chunks', { count: chunkRows(checked.rows).length })}</Banner>}
              </div>
              <button className="btn btn-primary" disabled={busy || !online || checked.problems.length > 0 || checked.rows.length === 0} onClick={() => void send()} data-testid="import-send">{busy ? t('measures.import.sending') : t('measures.import.send', { count: checked.rows.length })}</button>
            </Section>
          )}
          {result && (
            <>
              {result.failed ? <Banner tone="error"><span data-testid="import-failed">{result.failed}</span></Banner>
                : !busy && <Banner tone="ok"><span data-testid="import-done">{t('measures.import.done', { inserted: result.inserted, duplicates: result.duplicates })}</span></Banner>}
              <Link to="/measures" className="btn" data-testid="import-back">{t('measures.import.toNotebook')}</Link>
            </>
          )}
        </>
      )}
    </section>
  )
}
